import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { ZODIAC_SIGNS, getSunSign } from '@yadviga/shared'
import { Calendar, Clock, MapPin } from 'lucide-react'
import { useSession } from '../store/user.store'
import { Button } from '../components/ui/Button'
import { PageHeader } from '../components/ui/PageHeader'
import { BiorhythmsBlock } from '../components/biorhythms/BiorhythmsBlock'
import { BugReportSection } from '../components/feedback/BugReportSection'
import { PremiumStatusCard } from '../components/premium/PremiumStatusCard'
import { ReferralCard } from '../components/referral/ReferralCard'
import { ChannelSubscribeCard } from '../components/channel/ChannelSubscribeCard'
import { LinkingCard } from '../components/linking/LinkingCard'
import { HomeScreenButton } from '../components/profile/HomeScreenButton'
import { SelfDataModal } from '../components/profile/SelfDataModal'

/**
 * Single-scroll profile. Order top-to-bottom:
 *   1. PremiumStatusCard (with inline "У меня есть промокод" link)
 *   2. ChannelSubscribeCard (tap → open @yadviga_<lang> in TG)
 *   3. ReferralCard (compact widget → modal with share/copy)
 *   3. Биоритмы (current personal cycles)
 *   4. Свои данные (read-only summary, edit via SelfDataModal)
 *   5. LinkingCard (single button → modal with the redeem/generate UX)
 *   6. BugReportSection (compact link at the very bottom)
 *
 * Tabs were removed — every section is occasional-use, none of them are
 * heavy enough to need a separate tab. A single scroll with consistent
 * spacing reads cleaner than navigating between 3 mostly-empty surfaces.
 */
export function ProfileRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)

  if (!user) return null

  const language = (user.language ?? 'ru') as 'ru' | 'en'
  const sunSign = user.birthDate
    ? (() => {
        const [, m, d] = user.birthDate.split('-').map(Number)
        return getSunSign(m!, d!)
      })()
    : null
  const sunSignLabel = sunSign
    ? (ZODIAC_SIGNS.find((s) => s.id === sunSign)?.[language === 'en' ? 'nameEn' : 'nameRu'] ?? sunSign)
    : null
  const genderLabel = user.gender
    ? t(`profile.gender_${user.gender}`)
    : t('profile.gender_unspecified')

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader title={t('profile.title')} />

      <PremiumStatusCard />
      <ChannelSubscribeCard />
      <ReferralCard />

      {user.birthDate && <BiorhythmsBlock birthDate={user.birthDate} />}

      {/* Compact "Свои данные" — all editing happens in SelfDataModal. */}
      <section className="flex flex-col gap-3 rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif text-lg text-sage-800">{t('profile.identity_title')}</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditOpen(true)}
            leftIcon={
              <img
                src="/icons/action-edit.png"
                alt=""
                aria-hidden="true"
                className="h-4 w-4"
                draggable={false}
              />
            }
          >
            {t('actions.edit')}
          </Button>
        </div>

        <dl className="flex flex-col gap-1.5 text-sm">
          <Row label={t('profile.name')} value={user.name || <Dim>{t('profile.no_name')}</Dim>} />
          <Row label={t('profile.gender')} value={genderLabel} />
          <Row label={t('profile.language')} value={t(`profile.language_${language}`)} />
        </dl>

        <div className="border-t border-sage-200/60 pt-3">
          <h3 className="mb-2 text-sm font-medium text-sage-700">{t('profile.birth')}</h3>
          <ul className="flex flex-col gap-1.5 text-sm text-sage-700">
            <li className="flex items-center gap-2">
              <Calendar className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.8} aria-hidden />
              {user.birthDate ? (
                <span>
                  {user.birthDate}
                  {sunSignLabel && <span className="text-sage-500"> · {sunSignLabel}</span>}
                </span>
              ) : (
                <Dim>{t('profile.no_birth_date')}</Dim>
              )}
            </li>
            <li className="flex items-center gap-2">
              <Clock className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.8} aria-hidden />
              {user.birthTime ? <span>{user.birthTime}</span> : <Dim>{t('profile.no_birth_time')}</Dim>}
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.8} aria-hidden />
              {user.birthPlaceName ? <span>{user.birthPlaceName}</span> : <Dim>{t('profile.no_birth_place')}</Dim>}
            </li>
          </ul>
        </div>
      </section>

      <LinkingCard />

      <HomeScreenButton />

      <div className="mt-2 border-t border-sage-200/60 pt-4">
        <BugReportSection />
      </div>

      <SelfDataModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false)
          void qc.invalidateQueries()
        }}
      />
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-24 shrink-0 text-sage-500">{label}</dt>
      <dd className="text-sage-800">{value}</dd>
    </div>
  )
}

function Dim({ children }: { children: React.ReactNode }) {
  return <span className="text-sage-400">{children}</span>
}
